import { BASE_URL_API } from '@constants/base';
import { get, post } from '@utils/ajax';

/**
 * Shipping Request Service
 * Handles all shipping request related API calls
 */
const shippingRequestService = (() => {
    // Get shipping request list
    const getShippingRequestList = async params => {
        try {
            const response = await get(`${BASE_URL_API}/main/shippingRequest/list_api.php`, params, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Bulk register shipping request
    const bulkRegisterShippingRequest = async payload => {
        try {
            const response = await post(
                `${BASE_URL_API}/main/shippingRequest/approve_api.php`,
                {
                    method: 'f_create_multiple',
                    ...payload
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    // Get shipping request detail by ID
    const getShippingRequestDetail = async payload => {
        try {
            const response = await get(
                `${BASE_URL_API}/main/shippingRequest/detail_api.php`,
                {
                    method: 'info',
                    ...payload
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    const deleteShippingRequest = async payload => {
        try {
            const response = await post(`${BASE_URL_API}/main/shippingRequest/delete_api.php`, payload, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    const approveReturnRequest = async payload => {
        try {
            const response = await post(
                `${BASE_URL_API}/main/shippingRequest/approve_api.php`,
                {
                    method: 'accept_return',
                    ...payload
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    const rejectReturnRequest = async payload => {
        try {
            const response = await post(
                `${BASE_URL_API}/main/shippingRequest/approve_api.php`,
                {
                    method: 'reject_return',
                    ...payload
                },
                {
                    showError: true
                }
            );
            return response;
        } catch (error) {
            return error;
        }
    };

    const approveShippingRequest = async payload => {
        try {
            const response = await post(`${BASE_URL_API}/main/shippingRequest/approve_api.php`, payload, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    const updateShippedRequest = async payload => {
        try {
            const response = await post(`${BASE_URL_API}/main/shippingRequest/update_order_shipped.php`, payload, {
                showError: true
            });
            return response;
        } catch (error) {
            return error;
        }
    };

    // Export Excel file
    const exportExcel = async (queryParams = null) => {
        try {
            const qs =
                queryParams instanceof URLSearchParams
                    ? queryParams.toString()
                    : typeof queryParams === 'string'
                      ? queryParams
                      : '';
            const baseUrl = `${BASE_URL_API}/main/shippingRequest/export_excel.php`;
            const finalUrl = qs ? `${baseUrl}?${qs}` : baseUrl;

            const response = await fetch(finalUrl, {
                method: 'GET',
                credentials: 'include'
            });

            // Check if response is error (JSON format)
            const contentType = response.headers.get('content-type');
            if (!response.ok) {
                if (contentType && contentType.includes('application/json')) {
                    // Read JSON error response
                    const errorData = await response.json();
                    throw new Error(errorData?.message || 'Excelファイルのダウンロードに失敗しました。');
                }

                // If not JSON, throw generic error
                throw new Error('Excelファイルのダウンロードに失敗しました。');
            }

            // Get blob from response (Excel file)
            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date();
            const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
            const timeStr = String(date.getHours()).padStart(2, '0') + String(date.getMinutes()).padStart(2, '0') + String(date.getSeconds()).padStart(2, '0') + String(date.getMilliseconds()).padStart(3, '0');
            a.download = `発送依頼受付一覧_${dateStr}${timeStr}.xlsx`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { status: 'success', message: 'Excelファイルのダウンロードに成功しました。' };
        } catch (error) {
            return { status: 'error', message: error?.message || 'Excelファイルのダウンロードに失敗しました。' };
        }
    };

    // Import CSV
    const importCsv = async (file, options = {}) => {
        try {
            if (!file) {
                throw new Error('ファイルが選択されていません。');
            }
            if (!file.name.toLowerCase().endsWith('.csv')) {
                throw new Error('CSVファイルのみアップロードできます。');
            }

            const formData = new FormData();
            formData.append('csv_data', file, file.name);
            formData.append('method', 'upload');

            const response = await post(`${BASE_URL_API}/main/shippingRequest/import_csv.php`, formData, {
                isFormData: true,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json, text/javascript, */*; q=0.01',
                    'Accept-Language': 'en-US,en;q=0.9,ja;q=0.8,vi;q=0.7',
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache'
                },
                onProgress: options.onProgress ?? null,
                timeout: options.timeout ?? 30000,
                showError: true
            });

            return response;
        } catch (error) {
            return error;
        }
    };

    // Download template CSV
    const downloadTemplateCsv = async () => {
        try {
            const response = await fetch(`${BASE_URL_API}/main/shippingRequest/download_csv.php`, {
                method: 'GET',
                credentials: 'include'
            });

            // Check if response is error (JSON format)
            const contentType = response.headers.get('content-type');
            if (!response.ok) {
                if (contentType && contentType.includes('application/json')) {
                    // Read JSON error response
                    const errorData = await response.json();
                    throw new Error(errorData?.message || 'CSVファイルのダウンロードに失敗しました。');
                }

                // If not JSON, throw generic error
                throw new Error('CSVファイルのダウンロードに失敗しました。');
            }

            // Get blob from response (CSV file)
            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date();
            const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
            const timeStr = String(date.getHours()).padStart(2, '0') + String(date.getMinutes()).padStart(2, '0') + String(date.getSeconds()).padStart(2, '0') + String(date.getMilliseconds()).padStart(3, '0');
            a.download = `未登録発送依頼一覧_${dateStr}${timeStr}.csv`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { status: 'success', message: 'CSVファイルのダウンロードに成功しました。' };
        } catch (error) {
            return { status: 'error', message: error?.message || 'CSVファイルのダウンロードに失敗しました。' };
        }
    };

    return {
        getShippingRequestList,
        bulkRegisterShippingRequest,
        getShippingRequestDetail,
        deleteShippingRequest,
        approveReturnRequest,
        rejectReturnRequest,
        approveShippingRequest,
        updateShippedRequest,
        exportExcel,
        importCsv,
        downloadTemplateCsv
    };
})();

export default shippingRequestService;
